<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.selectbox"
          :label="local.attorney.type"
          :value.sync="formModel.attorneyTypeId"
          :items="attorneyTypes"
          item-text="name"
          :disabled="viewMode"
          item-value="id"
          validates="required"
          @change="attorneyTypeChanged"
        ></naj-input>
      </v-col>

      <v-col
        v-if="formModel.attorneyTypeId"
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.number"
          :label="`${isInternalAttorney ? local.attorney.number : local.attorney.approvalNumber}`"
          :value.sync="formModel.attorneyNumber"
          :validates="`required|long|max:14|isValid:${local.attorney.invalidNumber},${isNumberValid}|isVerified:${isVerified}`"
          :disabled="viewMode"
          @input="changeVerification(false)"
        ></naj-input>
      </v-col>

      <v-col
        cols="12"
        lg="3"
        class="d-flex align-center"
      >
        <template v-if="!isVerified">
          <naj-button
            square
            inputs-alignment
            :loading="isChecking"
            :disabled="!isCheckingValidityValid || viewMode"
            @click="validateAttorneyNumber"
          >
            {{ local.common.check }}
          </naj-button>
        </template>

        <naj-button
          v-if="formModel.isVerified"
          square
          inputs-alignment
          class="view-attorney-text"
          @click="showViewAttorneyModal"
        >
          {{ isInternalAttorney? local.attorney.attorneyText: local.attorney.attorneyDoc }}
        </naj-button>
      </v-col>
    </v-row>

    <v-row v-if="isExternalAttorney">
      <v-col
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.selectbox"
          :label="local.attorney.source"
          :value.sync="formModel.externalAttorneySourceId"
          :disabled="externalAttorneySourceDisabled"
          :items="countriesForNonSaudi"
          item-text="name"
          item-value="id"
          validates="required"
        ></naj-input>
      </v-col>

      <v-col
        cols="12"
        lg="2"
        align-self="center"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.attorney.isApprovedFromMofa"
          :value.sync="formModel.isApprovedFromMofa"
          disabled
        ></naj-input>
      </v-col>
      <v-col
        cols="12"
        lg="2"
        align-self="center"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.attorney.isApprovedFromMoj"
          :value.sync="formModel.isApprovedFromMoj"
          disabled
        ></naj-input>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        lg="2"
      >
        <naj-input
          :type-id="formFieldType.checkbox"
          :label="local.attorney.isRelative"
          :value.sync="formModel.isRelative"
          :disabled="viewMode"
        ></naj-input>
      </v-col>
      <v-col
        v-if="formModel.isRelative"
        cols="12"
        lg="3"
      >
        <naj-input
          :type-id="formFieldType.text"
          :label="local.attorney.relativeDescription"
          :value.sync="formModel.relativeDescription"
          :disabled="viewMode"
          validates="required"
        ></naj-input>
      </v-col>
    </v-row>

    <attorney-modal
      v-if="formModel.attorneyNumber"
      ref="viewAttorneyModal"
      :attorney-type="formModel.attorneyTypeId"
      :attorney-code="formModel.attorneyNumber.toString()"
      :agent-social-id="attorneySocialId"
      :client-social-id="relatedSocialId"
      :attorney-details="attorneyDetails"
      :unified-number="formModel.unifiedNumber"
      @onError="changeVerification(false)"
    ></attorney-modal>
  </div>
</template>

<script>
import { writingsOfJusticeEnum } from '@/helpers/enum.js';
import AttorneyModal from '@/modules/lawsuit/components/attorney-modal';
import { createNamespacedHelpers } from 'vuex';

const { mapState: mapLUTState, mapGetters: mapLUTGetters } = createNamespacedHelpers('LookupsModule');
const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');

export default {
  inject: ['$validator'],
  components: { AttorneyModal },
  props: ['formModel', 'attorneySocialId', 'relatedSocialId', 'viewMode'],
  data() {
    return {
      isChecking: false,
      externalAttorneySourceDisabled: true,
      attorneyDetails: {},
    };
  },
  computed: {
    ...mapLUTState(['attorneyTypes']),
    ...mapLUTGetters(['countriesForNonSaudi']),
    isCheckingValidityValid() {
      return this.formModel.attorneyNumber && this.attorneySocialId && this.relatedSocialId;
    },
    isNumberValid() {
      return this.formModel.attorneyNumber !== 0;
    },
    isVerified() {
      return this.formModel.isVerified;
    },
    isInternalAttorney() {
      // eslint-disable-next-line eqeqeq
      return this.formModel.attorneyTypeId == writingsOfJusticeEnum.internalAttorney;
    },
    isExternalAttorney() {
      // eslint-disable-next-line eqeqeq
      return this.formModel.attorneyTypeId == writingsOfJusticeEnum.externalAttorney;
    },
  },
  watch: {
    attorneySocialId() {
      this.changeVerification(false);
    },
    relatedSocialId() {
      this.changeVerification(false);
    },
    'formModel.socialId'() {
      this.changeVerification(false);
    },
    'formModel.commercialRegistrationNumber'() {
      this.changeVerification(false);
    },
    'formModel.unifiedNumber'() {
      this.changeVerification(false);
    },
  },
  mounted() {
    if (this.formModel.representationInfoDetailsChanged) {
      this.resetExternalAttorneyDetails();
    }
    this.$set(
      this.formModel,
      'attorneyTypeId',
      this.formModel.attorneyTypeId || writingsOfJusticeEnum.internalAttorney
    );
  },
  methods: {
    ...mapSysActions(['validateAgentAttorney']),
    validateAttorneyNumber() {
      this.isChecking = true;
      this.$set(this.formModel, 'isVerified', false);
      this.isExternalAttorney && this.resetExternalAttorneyDetails();

      const payload = {
        attorneyCode: this.formModel.attorneyNumber,
        agentSocialId: this.attorneySocialId,
        clientSocialId: this.relatedSocialId,
        managerSocialId: this.formModel.managerSocialId,
        commercialRegistrationNumber: this.formModel.commercialRegistrationNumber,
        unifiedNumber: this.formModel.unifiedNumber,
        attorneyType: this.formModel.attorneyTypeId,
      };
      this.validateAgentAttorney(payload)
        .then((res) => {
          this.$set(this, 'attorneyDetails', res);
          this.toaster.success(this.local.common.verificationSucceeded, 2);
          this.changeVerification(true);
          this.isExternalAttorney && this.setExternalAttorneyDetails();
        })
        .finally(() => (this.isChecking = false));
    },
    changeVerification(isVerified) {
      this.$set(this.formModel, 'isVerified', isVerified);
    },
    showViewAttorneyModal() {
      this.$refs.viewAttorneyModal.show();
    },
    setExternalAttorneyDetails() {
      this.$set(this.formModel, 'approvalNumber', this.attorneyDetails.attorneyNumber);
      this.$set(this.formModel, 'isApprovedFromMofa', this.attorneyDetails.isApprovedFromMofa);
      this.$set(this.formModel, 'isApprovedFromMoj', true);
      const attorneyCountry = (this.countriesForNonSaudi || []).find(a => a.name == this.attorneyDetails.kharjiaCountry);
      if (attorneyCountry) {
        this.externalAttorneySourceDisabled = true;
        this.$set(this.formModel, 'externalAttorneySourceId', attorneyCountry.id);
      } else {
        this.externalAttorneySourceDisabled = false;
      }

    },
    resetExternalAttorneyDetails() {
      this.$set(this, 'attorneyDetails', null);
      this.$set(this.formModel, 'approvalNumber', null);
      this.$set(this.formModel, 'isApprovedFromMofa', null);
      this.$set(this.formModel, 'isApprovedFromMoj', null);
      this.$set(this.formModel, 'externalAttorneySourceId', null);
      this.changeVerification(false);
    },
    attorneyTypeChanged() {
      this.changeVerification(false);
      this.isExternalAttorney && this.resetExternalAttorneyDetails();
    },
  },
};
</script>
